import React, { useMemo, useState } from 'react';
import { Box, Typography, Stack, Button, LinearProgress, TextField, InputAdornment } from '@mui/material';
import { ChevronRight, Search, FileDownload } from '@mui/icons-material';

// Cents, deliberately. Whole-dollar rounding is what made a £4,500 payment read as
// an impossibly clean "$6,000" (it is $6,000.26) and sent the reader hunting for a
// bug that wasn't there. At transaction level the exact figure is the honest one.
const formatUSD = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v || 0);

// The row's own currency, as actually paid. USD rows are skipped (the USD column
// already says it) and mixed-currency groups arrive with native_currency = null.
// A bad/unknown ISO code makes Intl throw — fall back to "CODE 1,234.56".
function formatNative(row) {
  const { native_currency: ccy, native_amount: amt } = row;
  if (!ccy || ccy === 'USD' || amt == null) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: ccy, maximumFractionDigits: 2,
    }).format(amt);
  } catch {
    return `${ccy} ${Number(amt).toFixed(2)}`;
  }
}

const MIXED_EXPLANATION =
  'This group was billed in more than one currency, so there is no single native '
  + 'amount to show. Summing across currencies would be meaningless. Drill in to see '
  + 'each transaction in its own currency.';

function nativeExplanation(row) {
  return row.fx_rate
    ? `Paid in ${row.native_currency}. Converted to USD at ${Number(row.fx_rate).toFixed(4)} — `
      + 'the monthly average rate, not the rate this payment settled at.'
    : `Paid in ${row.native_currency}. No FX rate available for this period.`;
}

function exportAggregateCsv(rows, filename) {
  const header = ['label', 'native_currency', 'native_amount', 'fx_rate', 'amount_usd', 'count'];
  const lines = [header.join(',')];
  rows.forEach((r) => {
    const fields = header.map((h) => {
      const v = r[h] ?? '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    });
    lines.push(fields.join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * `rowAction(row) => ReactNode` adds a per-row action button (e.g. Reclassify).
 * The button must call `e.stopPropagation()` to avoid triggering the row drill.
 */
export default function AggregateView({ data, onRowClick, onLoadMore, loadingMore, rowAction }) {
  const total = data.total_usd || 0;
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q) return data.rows;
    const ql = q.toLowerCase();
    return data.rows.filter((r) => (r.label || '').toLowerCase().includes(ql));
  }, [data.rows, q]);
  const filteredTotal = useMemo(
    () => filtered.reduce((s, r) => s + (Number(r.amount_usd) || 0), 0),
    [filtered],
  );
  const showFilteredCount = q && filtered.length !== data.rows.length;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Filter…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Button
          size="small"
          startIcon={<FileDownload />}
          onClick={() => exportAggregateCsv(filtered, `${(data.title || 'drill').replace(/[^a-z0-9-]+/gi, '_')}.csv`)}
        >
          CSV
        </Button>
      </Stack>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {filtered.length} {filtered.length === 1 ? 'row' : 'rows'}
          {showFilteredCount ? ` of ${data.rows.length}` : ''}
          {' • '}Total {formatUSD(showFilteredCount ? filteredTotal : total)}
        </Typography>
      </Box>
      <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
        {filtered.map((r) => {
          const pct = total !== 0 ? (Math.abs(r.amount_usd) / Math.abs(total)) * 100 : 0;
          const native = formatNative(r);
          return (
            <Box
              key={r.id}
              onClick={() => r.drillable && onRowClick && onRowClick(r)}
              sx={{
                py: 1.25,
                px: 1,
                cursor: r.drillable ? 'pointer' : 'default',
                '&:hover': r.drillable ? { backgroundColor: 'rgba(33, 150, 243, 0.05)' } : {},
                borderRadius: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.label}
                  </Typography>
                  {(r.count != null || native || r.currency_mixed) && (
                    <Typography variant="caption" color="text.secondary">
                      {r.count != null && `${r.count} ${r.count === 1 ? 'item' : 'items'}`}
                      {r.count != null && (native || r.currency_mixed) && ' · '}
                      {native && (
                        <Box
                          component="span"
                          aria-label={nativeExplanation(r)}
                          title={nativeExplanation(r)}
                          sx={{ fontWeight: 500, color: 'text.primary' }}
                        >
                          {native}
                          {r.fx_rate ? ` @ ${Number(r.fx_rate).toFixed(4)} avg` : ''}
                        </Box>
                      )}
                      {/* Without this, a suppressed native reads as a missing one — the
                          reader can't tell "we chose not to sum across currencies" from
                          "the feature didn't fire here". */}
                      {!native && r.currency_mixed && (
                        <Box
                          component="span"
                          aria-label={MIXED_EXPLANATION}
                          title={MIXED_EXPLANATION}
                          sx={{ fontStyle: 'italic' }}
                        >
                          mixed currencies
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: r.amount_usd < 0 ? 'error.main' : 'inherit',
                    }}
                  >
                    {formatUSD(r.amount_usd)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pct.toFixed(1)}%
                  </Typography>
                </Box>
                {rowAction && rowAction(r)}
                {r.drillable && <ChevronRight sx={{ color: 'text.secondary', fontSize: 18 }} />}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, pct)}
                sx={{ mt: 0.5, height: 3, borderRadius: 1, opacity: 0.5 }}
              />
            </Box>
          );
        })}
      </Stack>
      {data.has_more && onLoadMore && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button onClick={onLoadMore} variant="outlined" size="small" disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
      {data.has_more && !onLoadMore && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing top {data.rows.length} by amount; pagination coming in a follow-up.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
